<template>
  <div class="challenges-wrapper">
    <div v-if="profile.status === 'Available'" class="challenges-container">
      <!-- Enhanced Header with Professional Design -->
      <div class="modern-header" style="position: relative; z-index: 1">
        <div class="container">
          <div class="header-content">
            <div class="header-left">
              <div class="page-title">
                <div class="title-icon">
                  <v-icon class="header-icon" size="48">mdi-trophy</v-icon>
                </div>
                <div class="title-text">
                  <h1 class="page-title-text">Team Challenges</h1>
                  <p class="page-subtitle">
                    Manage and participate in competitive challenges
                  </p>
                </div>
              </div>
            </div>
            <div class="header-right">
              <div class="actions-wrapper">
                <!-- Advanced Search -->
                <div class="search-container">
                  <v-icon class="search-icon">mdi-magnify</v-icon>
                  <input v-model="searchQuery" type="text" placeholder="Search challenges..." class="search-input" />
                </div>

                <!-- Accordion Filter Panel -->
                <div class="filter-group">
                  <div class="filter-dropdown" @click="toggleAccordionFilter">
                    <button class="filter-btn">
                      <v-icon size="18" color="#ffff">mdi-filter-variant</v-icon>
                      <span>Filters</span>
                      <v-icon size="16" :class="{ 'rotate-180': showAccordionFilter }">mdi-chevron-down</v-icon>
                    </button>
                    <div v-if="showAccordionFilter" class="filter-menu" @click.stop>
                      <!-- Accordion Section: Status -->
                      <div class="accordion-section">
                        <div class="accordion-header" @click="toggleAccordion('status')">
                          <span>Status</span>
                          <v-icon size="16">{{
                            accordionOpen.status
                              ? "mdi-chevron-up"
                              : "mdi-chevron-down"
                          }}</v-icon>
                        </div>
                        <div v-show="accordionOpen.status" class="accordion-content">
                          <div v-for="status in statusOptions" :key="status.value"
                            @click="toggleStatusFilter(status.value)" class="filter-item" :class="{
                              'active-filter': selectedStatuses.includes(
                                status.value
                              )
                            }">
                            <div class="status-indicator" :class="status.class"></div>
                            <span>{{ status.text }}</span>
                            <v-icon v-if="selectedStatuses.includes(status.value)" size="16" class="check-icon">
                              mdi-check
                            </v-icon>
                          </div>
                          <button @click="clearStatusFilter" class="clear-btn">
                            Clear
                          </button>
                        </div>
                      </div>
                      <!-- Accordion Section: Date -->
                      <div class="accordion-section">
                        <div class="accordion-header" @click="toggleAccordion('date')">
                          <span>Date</span>
                          <v-icon size="16">{{
                            accordionOpen.date
                              ? "mdi-chevron-up"
                              : "mdi-chevron-down"
                          }}</v-icon>
                        </div>
                        <div v-show="accordionOpen.date" class="accordion-content">
                          <div class="date-inputs">
                            <div class="date-input-group">
                              <label>Start Date</label>
                              <input v-model="startDate" type="date" class="date-input" />
                            </div>
                            <div class="date-input-group">
                              <label>End Date</label>
                              <input v-model="endDate" type="date" class="date-input" />
                            </div>
                          </div>
                          <div class="quick-filters">
                            <button v-for="preset in datePresets" :key="preset.value"
                              @click="applyDatePreset(preset.value)" class="preset-btn" :class="{
                                active: currentDatePreset === preset.value
                              }">
                              {{ preset.text }}
                            </button>
                          </div>
                          <button @click="clearDateFilter" class="clear-btn">
                            Clear
                          </button>
                        </div>
                      </div>
                      <!-- Accordion Section: Sort -->
                      <div class="accordion-section">
                        <div class="accordion-header" @click="toggleAccordion('sort')">
                          <span>Sort</span>
                          <v-icon size="16">{{
                            accordionOpen.sort
                              ? "mdi-chevron-up"
                              : "mdi-chevron-down"
                          }}</v-icon>
                        </div>
                        <div v-show="accordionOpen.sort" class="accordion-content">
                          <div v-for="option in sortOptions" :key="option.value" @click="applySorting(option.value)"
                            class="filter-item" :class="{
                              'active-filter': currentSort === option.value
                            }">
                            <v-icon size="16">{{ option.icon }}</v-icon>
                            <span>{{ option.text }}</span>
                            <v-icon v-if="currentSort === option.value" size="16" class="check-icon">
                              mdi-check
                            </v-icon>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <!-- History Button -->
                <button @click="history" class="action-btn history-btn" v-if="
                  profile.type === 'Agency' || profile.type === 'Volunteer'
                ">
                  <v-icon size="18" color="#fff">mdi-history</v-icon>
                  <span class="btn-text">History</span>
                </button>

                <!-- Create Button -->
                <div class="create-section">
                  <button @click="createCompetition" class="create-btn" v-if="profile.type === 'Agency'">
                    <v-icon size="18" color="#ffff">mdi-plus</v-icon>
                    <span class="create-btn-text">Create</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Active Filters Display -->
          <div v-if="hasActiveFilters" class="active-filters">
            <div class="filter-chips">
              <div v-if="searchQuery" class="filter-chip">
                <span>Search: "{{ searchQuery }}"</span>
                <button @click="searchQuery = ''" class="chip-remove">
                  <v-icon size="14">mdi-close</v-icon>
                </button>
              </div>
              <div v-for="status in selectedStatuses" :key="status.value" class="filter-chip">
                <span>{{ getStatusText(status) }}</span>
                <button @click="removeStatusFilter(status)" class="chip-remove">
                  <v-icon size="14">mdi-close</v-icon>
                </button>
              </div>
              <div v-if="startDate || endDate" class="filter-chip">
                <span>
                  {{ startDate ? formatDate(startDate) : "Start" }} -
                  {{ endDate ? formatDate(endDate) : "End" }}
                </span>
                <button @click="clearDateFilter" class="chip-remove">
                  <v-icon size="14">mdi-close</v-icon>
                </button>
              </div>
              <button @click="clearAllFilters" class="clear-all-btn">
                Clear All
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Results Summary -->
      <div class="results-summary">
        <div class="container">
          <div class="summary-content">
            <div class="results-count">
              <span class="count">{{ filteredCompetitions.length }}</span>
              <span class="label">{{
                filteredCompetitions.length === 1 ? "Challenge" : "Challenges"
              }}
                found</span>
            </div>
            <div class="summary-stats">
              <div class="stat-item">
                <span class="stat-value">{{ ongoingCount }}</span>
                <span class="stat-label">Ongoing</span>
              </div>
              <div class="stat-item">
                <span class="stat-value">{{ upcomingCount }}</span>
                <span class="stat-label">Upcoming</span>
              </div>
              <!-- <div class="stat-item">
              <span class="stat-value">{{ completedCount }}</span>
              <span class="stat-label">Completed</span>
            </div> -->
            </div>
          </div>
        </div>
      </div>

      <!-- Main Content Area -->
      <div class="main-content">
        <!-- Loading State -->
        <div v-if="loading" class="loading-state">
          <div class="skeleton-card" v-for="i in 6" :key="i">
            <div class="skeleton-header"></div>
            <div class="skeleton-content">
              <div class="skeleton-line"></div>
              <div class="skeleton-line short"></div>
              <div class="skeleton-line"></div>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else-if="!loading && filteredCompetitions.length === 0" class="empty-state">
          <div class="empty-content">
            <v-icon class="empty-icon" size="80">mdi-trophy-outline</v-icon>
            <h3>
              {{
                searchQuery || hasActiveFilters
                  ? "No Matches Found"
                  : "No Challenges Yet"
              }}
            </h3>
            <p v-if="searchQuery || hasActiveFilters">
              Try adjusting your search or filters to find what you're looking
              for
            </p>
            <p v-else-if="profile.type === 'Agency'">
              Create your first team challenge to get started
            </p>
            <p v-else>No challenges available at the moment</p>

            <div class="empty-actions">
              <button v-if="searchQuery || hasActiveFilters" @click="clearAllFilters" class="secondary-btn">
                Clear Filters
              </button>
              <button @click="createCompetition" class="create-btn" v-if="profile.type === 'Agency'">
                <v-icon size="18" color="#ffff">mdi-plus</v-icon>
                Create Challenge
              </button>
            </div>
          </div>
        </div>

        <!-- Challenges Grid/List -->
        <div v-else :class="[viewMode === 'grid' ? 'challenges-grid' : 'challenges-list']">
          <div v-for="competition in paginatedCompetitions" :key="competition._id" :class="[
            viewMode === 'grid' ? 'challenge-card' : 'challenge-list-item',
            { 'expired-card': isExpired(competition) }
          ]">
            <!-- Card/List Item Content -->
          <div class="card-header" :class="getStatusClass(competition)">
  <div class="status-info">
    <div class="status-badge">
      <v-icon size="14" class="status-icon" color="#ffff">{{
        getStatusIcon(competition.competition_status)
      }}</v-icon>
      {{ competition.competition_status }}
    </div>
    <div v-if="isExpired(competition)" class="expired-badge">
      Expired
    </div>
    <!-- Show Join Type badge -->
    <div v-if="getUserJoinType(competition)" class="join-type-badge">
      <v-icon size="12" color="#0895F9">mdi-account-plus</v-icon>
      {{ getUserJoinType(competition) }}
    </div>
  </div>

  <!-- Card Actions -->
  <div class="card-actions" @click.stop>
    <v-tooltip bottom>
      <template v-slot:activator="{ on, attrs }">
        <button @click="detailedView(competition)" class="info-btn" v-bind="attrs" v-on="on">
          <v-icon size="28" color="primary">mdi mdi-information-slab-circle-outline</v-icon>
        </button>
      </template>
      <span>Detailed View</span>
    </v-tooltip>

    <v-tooltip bottom>
      <template v-slot:activator="{ on, attrs }">
        <button v-if="canEdit(competition)" @click="onEdit(competition)" class="action-btn edit-btn"
          v-bind="attrs" v-on="on">
          <v-icon size="16" color="#fff">mdi-pencil</v-icon>
        </button>
      </template>
      <span>Edit Challenge</span>
    </v-tooltip>

    <v-tooltip bottom>
      <template v-slot:activator="{ on, attrs }">
        <button v-if="canAddAgencies(competition)" @click="onAddAgencies(competition)"
          class="action-btn add-agencies-btn" v-bind="attrs" v-on="on">
          <v-icon size="16" color="#fff">mdi-account-multiple-plus</v-icon>
        </button>
      </template>
      <span>Add Agencies</span>
    </v-tooltip>

    <v-tooltip bottom>
      <template v-slot:activator="{ on, attrs }">
        <button v-if="canDelete(competition)" @click="onDelete(competition)" class="action-btn delete-btn"
          v-bind="attrs" v-on="on">
          <v-icon size="16" color="#fff">mdi-delete</v-icon>
        </button>
      </template>
      <span>Delete Challenge</span>
    </v-tooltip>

    <!-- Show Accept/Reject for Invited users with Pending status -->
    <template v-if="
      !isCreator(competition) && 
      getUserJoinType(competition) === 'Invitation' && 
      getUserStatusFromHistory(competition) === 'Pending'
    ">
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <button 
            @click="acceptInvitation(competition)" 
            class="action-btn accept-btn"
            v-bind="attrs" 
            v-on="on"
            :disabled="confirmingResponse == competition._id"
          >
            <v-icon size="18" color="#fff">mdi-check</v-icon>
            <span class="action-btn-text">Accept</span>
          </button>
        </template>
        <span>Accept Invitation</span>
      </v-tooltip>

      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <button 
            @click="rejectInvitation(competition)" 
            class="action-btn reject-btn"
            v-bind="attrs" 
            v-on="on"
            :disabled="rejectResponse == competition._id"
          >
            <v-icon size="18" color="#fff">mdi-close</v-icon>
            <span class="action-btn-text">Reject</span>
          </button>
        </template>
        <span>Reject Invitation</span>
      </v-tooltip>
    </template>

    <!-- Single Join Status Display - Only show for non-creators -->
    <template v-else-if="!isCreator(competition) && (profile.type === 'Volunteer' || profile.type === 'Agency')">
      <!-- Check if user has a status in volunteerHistory -->
      <div v-if="getUserStatusFromHistory(competition)" class="join-status-badge"
        :class="getJoinStatusClassFromStatus(getUserStatusFromHistory(competition))">
        <v-icon size="14" class="status-icon">
          {{ getStatusIconForJoin(getUserStatusFromHistory(competition)) }}
        </v-icon>
        {{ getUserStatusFromHistory(competition) }}
      </div>

      <!-- Otherwise show join button if eligible -->
      <v-tooltip bottom v-else-if="canJoin(competition)">
        <template v-slot:activator="{ on, attrs }">
          <button @click="join(competition)" class="action-btn join-btn" v-bind="attrs" v-on="on">
            <v-icon size="18" color="#fff">mdi-account-plus</v-icon>
            <span class="join-btn-text">JOIN</span>
          </button>
        </template>
        <span>Join Challenge</span>
      </v-tooltip>
    </template>
  </div>
</div>

            <div class="card-content" @click="detailedView(competition)">
              <!-- <div class="challenge-icon">
                <v-icon
                  :class="['challenge-icon', getStatusClass(competition)]"
                  size="32"
                >
                  {{ getStatusIcon(competition.competition_status) }}
                </v-icon>
              </div> -->

              <div class="challenge-details" @click="detailedView(competition)">
                <h2 class="challenge-title">{{ competition.title }}</h2>
                <p class="challenge-description">
                  {{ getDescription(competition) }}
                  <button v-if="competition.description.length > 100" @click.stop="toggleDescription(competition)"
                    class="toggle-btn">
                    {{
                      competition.showFullDescription
                        ? "Show Less"
                        : "Read More"
                    }}
                  </button>
                </p>
                <div class="creator-info">
                  <div class="creator-avatar">
                    <img v-if="competition.createdBy?.midThumbnail" :src="competition.createdBy.midThumbnail"
                      :alt="competition.createdBy.name" @click="goToProfile(competition.createdBy?._id)" />
                    <v-icon v-else size="18">mdi-account</v-icon>
                  </div>
                  <div class="creator-details">
                    <span class="creator-name" @click="goToProfile(competition.createdBy?._id)">{{
                      truncateName(competition.createdBy?.name) }}</span>
                    <!-- <span class="creator-type">{{
                      competition.createdBy?.type || "Agency"
                    }}</span> -->
                  </div>
                </div>
              </div>
            </div>

            <div class="divider"></div>

            <div class="challenge-metadata">
              <div class="date-info">
                <div class="date-item">
                  <v-icon size="16" class="date-icon" color="#1da1f2">mdi-calendar-start</v-icon>
                  <div class="date-details">
                    <span class="date-label">Starts</span>
                    <span class="date-value">{{
                      formatDate(competition.start_date)
                      }}</span>
                  </div>
                </div>
                <div class="date-item">
                  <v-icon size="16" class="date-icon" color="#1da1f2">mdi-calendar-end</v-icon>
                  <div class="date-details">
                    <span class="date-label">Ends</span>
                    <span class="date-value">{{
                      formatDate(competition.end_date)
                      }}</span>
                  </div>
                </div>
                <!-- <div class="date-item duration">
                <v-icon size="16" class="date-icon">mdi-clock-outline</v-icon>
                <div class="date-details">
                  <span class="date-label">Duration</span>
                  <span class="date-value">{{ getDuration(competition) }}</span>
                </div>
              </div> -->
              </div>

              <div>
                <div v-if="
                  competition.highestAgency || competition.highestVolunteer
                " class="elite-leaderboard">
                  <h3 class="leaderboard-title">🏆 Elite Leaderboard</h3>
                  <div class="leaderboard-row">
                    <!-- Agency Card -->
                    <div v-if="competition.highestAgency" class="liquid-card agency" @click="agencyScores(competition)">
                      <div class="liquid-icon">🏛️</div>
                      <div class="liquid-details">
                        <span>Top Agency</span>
                        <strong>{{
                          truncateName(competition.highestAgency.name)
                          }}</strong>
                      </div>
                      <div class="liquid-points">
                        {{ competition.highestAgency.points }}<small>pts</small>
                      </div>
                    </div>
                    <!-- Volunteer Card -->
                    <div v-if="competition.highestVolunteer" class="liquid-card volunteer"
                      @click="agencyScores(competition)">
                      <div class="liquid-icon">🌟</div>
                      <div class="liquid-details">
                        <span>Top Volunteer</span>
                        <strong>{{
                          truncateName(competition.highestVolunteer.name)
                          }}</strong>
                      </div>
                      <div class="liquid-points">
                        {{ competition.highestVolunteer.points
                        }}<small>pts</small>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-else class="empty-leaderboard">
                  <span v-if="getStatusClass(competition) === 'status-ongoing'">
                    The challenge is active, are you?
                  </span>
                  <span v-else>
                    Get ready challenge is coming up!
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Pagination -->
        <div v-if="totalPages > 1" class="pagination">
          <button @click="currentPage = Math.max(1, currentPage - 1)" :disabled="currentPage === 1"
            class="pagination-btn">
            <v-icon size="16">mdi-chevron-left</v-icon>
            Previous
          </button>

          <div class="page-numbers">
            <button v-for="page in visiblePages" :key="page" @click="currentPage = page"
              :class="['page-btn', { active: currentPage === page }]">
              {{ page }}
            </button>
          </div>

          <button @click="currentPage = Math.min(totalPages, currentPage + 1)" :disabled="currentPage === totalPages"
            class="pagination-btn">
            Next
            <v-icon size="16">mdi-chevron-right</v-icon>
          </button>
        </div>
      </div>

      <!-- Snackbar for notifications -->
      <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="5000" bottom right shaped>
        <div class="snackbar-content">
          <v-icon size="20" class="snackbar-icon">
            {{
              snackbarColor === "success"
                ? "mdi-check-circle"
                : "mdi-alert-circle"
            }}
          </v-icon>
          <span>{{ errorMessage }}</span>
        </div>
        <template v-slot:action="{ attrs }">
          <v-btn icon v-bind="attrs" @click="snackbar = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </template>
      </v-snackbar>

      <!-- Dialogs -->
      <create-competitions :dialog="createDialog" :onClose="onCloseDialog" v-if="createDialog" attach="body" />
      <edit-competition :dialog="editDialog" :onClose="onCloseDialog" :respectiveCompetition="respectiveCompetition"
        v-if="editDialog" />
      <delete-competition :dialog="deleteDialog" :onClose="onCloseDialog" :respectiveCompetition="respectiveCompetition"
        v-if="deleteDialog" />
      <join-competition :dialog="joinDialog" :onClose="onCloseDialog" :currentCompetition="currentCompetition"
        v-if="joinDialog" />
      <overallagency-scores :dialog="overallagencyScoresDialog" :onClose="onCloseDialog" :topScoresData="topScoresData"
        v-if="overallagencyScoresDialog" />
      <!-- Add Agencies Dialog -->
      <add-agencies-to-competition :dialog="addAgenciesDialog" :onClose="onCloseDialog"
        :competition="currentCompetition" v-if="addAgenciesDialog" />
    </div>

    <!-- Account Status Message -->
    <div v-if="
      profile &&
      (profile.status === 'Rejected' || profile.status === 'Pending')
    " class="status-message">
      <div class="status-card">
        <v-icon class="status-icon" size="64">mdi-clock-outline</v-icon>
        <h2>Account Under Review</h2>
        <p>Please wait for approval/confirmation from Admin</p>
        <button @click="$router.push('/')" class="back-btn">
          <v-icon size="18">mdi-home</v-icon>
          Back to Home
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import CreateCompetitions from "./CreateCompetitons.vue";
import DeleteCompetition from "./DeleteCompetition.vue";
import EditCompetition from "./EditCompetition.vue";
import joinCompetition from "./CompetitionJoinDialog.vue";
import overallagencyScores from "../competition/OverallAgencyScore.vue";
import AddAgenciesToCompetition from './AddAgenciesToCompetition.vue';


export default {
  name: "TeamChallenges",
  components: {
    CreateCompetitions,
    EditCompetition,
    DeleteCompetition,
    joinCompetition,
    overallagencyScores,
    AddAgenciesToCompetition
  },
  data() {
    return {
      loading: false,

    // Add these for loading states on accept/reject buttons
      confirmingResponse: "",
      rejectResponse: "",

      // Search and filters
      searchQuery: "",
      selectedStatuses: [],
      startDate: "",
      endDate: "",
      currentDatePreset: "",
      currentSort: "status",

      // UI state
      viewMode: "grid",
      showStatusFilter: false,
      showDateFilter: false,
      showSortFilter: false,
      showAccordionFilter: false,
      accordionOpen: {
        status: true,
        date: false,
        sort: false
      },

      // Pagination
      currentPage: 1,
      itemsPerPage: 5,

      // Filter options
      statusOptions: [
        { text: "All Status", value: "all", class: "status-all" },
        { text: "Upcoming", value: "Upcoming", class: "status-upcoming" },
        { text: "Ongoing", value: "Ongoing", class: "status-ongoing" }
        //  { text: "Completed", value: "Completed", class: "status-completed" },
      ],

      datePresets: [
        { text: "Today", value: "today" },
        { text: "This Week", value: "week" },
        { text: "This Month", value: "month" },
        { text: "Next 30 Days", value: "next30" }
      ],

      sortOptions: [
        { text: "Status", value: "status", icon: "mdi-sort-variant" },
        {
          text: "Newest First",
          value: "newest",
          icon: "mdi-sort-calendar-descending"
        },
        {
          text: "Oldest First",
          value: "oldest",
          icon: "mdi-sort-calendar-ascending"
        },
        {
          text: "Title A-Z",
          value: "title-asc",
          icon: "mdi-sort-alphabetical-ascending"
        },
        {
          text: "Title Z-A",
          value: "title-desc",
          icon: "mdi-sort-alphabetical-descending"
        },
      ],

      // Notifications
      errorMessage: null,
      snackbar: false,
      snackbarColor: "error",

      // Dialogs
      createDialog: false,
      overallagencyScoresDialog: false,
      addAgenciesDialog: false,
      agencyScoreDetails: null,
      volunteerScoreDetails: null,
      editDialog: false,
      deleteDialog: false,
      joinDialog: false,
      respectiveCompetition: null,
      currentCompetition: null,
      joined: false,
      topScoresData: {
        topScoresofAgency: [],
        topScoresofVolunteers: []
      }
    };
  },
  computed: {
    ...mapState("auth", {
      profile: "profile"
    }),
    ...mapState("competition", {
      competitions: "competitions"
    }),
    ...mapGetters("competition", {
      getAllCompetitions: "getAllCompetitions"
    }),
    filteredCompetitions() {
      //console.log('Filtered Competitions: ', this.getAllCompetitions);
      let filtered = [...this.getAllCompetitions]; // keep original order
      //console.log('Initial Competitions: ', filtered);
      // Search filter
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(
          comp =>
            comp.title.toLowerCase().includes(query) ||
            comp.description.toLowerCase().includes(query) ||
            comp.createdBy?.name.toLowerCase().includes(query)
        );
      }

      // Status filter
      if (
        this.selectedStatuses.length > 0 &&
        !this.selectedStatuses.includes("all")
      ) {
        filtered = filtered.filter(comp =>
          this.selectedStatuses.includes(comp.competition_status)
        );
      }

      // Date range filter
      if (this.startDate) {
        filtered = filtered.filter(
          comp => new Date(comp.start_date) >= new Date(this.startDate)
        );
      }
      if (this.endDate) {
        filtered = filtered.filter(
          comp => new Date(comp.end_date) <= new Date(this.endDate)
        );
      }

      // Sorting
      if (this.currentSort === "newest") {
        filtered = filtered
          .slice()
          .sort((a, b) => new Date(b.start_date) - new Date(a.start_date));
      } else if (this.currentSort === "oldest") {
        filtered = filtered
          .slice()
          .sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
      } else if (this.currentSort === "title-asc") {
        filtered = filtered
          .slice()
          .sort((a, b) => a.title.localeCompare(b.title));
      } else if (this.currentSort === "title-desc") {
        filtered = filtered
          .slice()
          .sort((a, b) => b.title.localeCompare(a.title));
      } else if (this.currentSort === "status") {
        filtered = filtered
          .slice()
          .sort((a, b) =>
            a.competition_status.localeCompare(b.competition_status)
          );
      }
      // If no sort, keep as is (original order)

      return filtered;
    },

    paginatedCompetitions() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredCompetitions.slice(start, end);
    },
    totalPages() {
      return Math.ceil(this.filteredCompetitions.length / this.itemsPerPage);
    },
    visiblePages() {
      const pages = [];
      const start = Math.max(1, this.currentPage - 2);
      const end = Math.min(this.totalPages, this.currentPage + 2);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    },
    hasActiveFilters() {
      return (
        this.searchQuery ||
        this.selectedStatuses.length > 0 ||
        this.startDate ||
        this.endDate
      );
    },
    ongoingCount() {
      return this.filteredCompetitions.filter(
        c => c.competition_status === "Ongoing"
      ).length;
    },
    upcomingCount() {
      return this.filteredCompetitions.filter(
        c => c.competition_status === "Upcoming"
      ).length;
    },
    completedCount() {
      return this.filteredCompetitions.filter(
        c => c.competition_status === "Completed"
      ).length;
    }
  },
  methods: {
    ...mapActions({
      fetchEachCompetitions: "competition/fetchEachCompetitions",
      fetchAllCompetitions: "competition/fetchAllCompetitions",
      joinCompetition: "members/joinCompetition",
      checkUserExistinCompetition: "competition/checkUserExistinCompetition",
      topScores: "competition/topScores",
      respondToCompetitionRequest: "competition/respondToCompetitionRequest" 

    }),
    // Notification methods
    showErrorMessage(message) {
      this.errorMessage = message;
      this.snackbar = true;
      this.snackbarColor = "error";
    },
    showSuccessMessage(message) {
      this.errorMessage = message;
      this.snackbar = true;
      this.snackbarColor = "success";
    },
    // Dialog methods
    createCompetition() {
      this.createDialog = true;
    },
    onCloseDialog() {
      this.createDialog = false;
      this.editDialog = false;
      this.deleteDialog = false;
      this.joinDialog = false;
      this.addAgenciesDialog = false;
      this.overallagencyScoresDialog = false;
      this.loadCompetitions();
    },
    // Navigation methods
    history() {
      this.$router.push({ name: "history" });
    },
    detailedView(competition) {
      if (this.profile.type === "Agency") {
        this.$router.push({
          name: "detailed-view",
          params: { id: competition._id }
        });
      } else {
        this.$router.push({
          name: "history"
        });
      }
    },
    getAgencyJoinStatus(competition) {
      if (
        this.profile.type === "Agency" &&
        Array.isArray(competition.requested_agency_ids)
      ) {
        const req = competition.requested_agency_ids.find(
          r =>
            r.user_id === this.profile._id ||
            (r.user_id && r.user_id.$oid === this.profile._id)
        );
        if (!req) return null;

        // Check for explicit status
        if (req.status) {
          const status = req.status.toLowerCase();
          if (status === "rejected") return "Rejected";
          if (status === "approved") return "Approved";
          if (status === "pending") return "Pending";
          return req.status;
        }

        // Fallback to approved boolean
        if (req.approved === true) return "Approved";
        if (req.approved === false) return "Rejected";
        return "Pending";
      }
      return null;
    },
    // Competition actions
    onEdit(competition) {
      this.respectiveCompetition = competition;
      this.editDialog = true;
    },
    onDelete(competition) {
      this.respectiveCompetition = competition;
      this.deleteDialog = true;
    },
    truncateName(name, length = 20) {
      if (!name) return "";
      return name.length > length ? name.slice(0, length) + "..." : name;
    },
    // Accept Invitation
async acceptInvitation(competition) {
  const params = {
    competition_id: competition._id,
    response_status: "Accepted"
  };
  try {
    this.confirmingResponse = competition._id;
    await this.respondToCompetitionRequest(params);
    
    // Update the local competition data
    this.updateCompetitionStatus(competition, "Accepted");
    
    this.showSuccessMessage("🎉 Invitation accepted successfully!");
    await this.loadCompetitions();
  } catch (error) {
    this.showErrorMessage(error?.response?.data?.message || "Failed to accept invitation.");
  } finally {
    this.confirmingResponse = "";
  }
},

// Reject Invitation
async rejectInvitation(competition) {
  const params = {
    competition_id: competition._id,
    response_status: "Rejected"
  };
  try {
    this.rejectResponse = competition._id;
    await this.respondToCompetitionRequest(params);
    
    // Update the local competition data
    this.updateCompetitionStatus(competition, "Rejected");
    
    this.showSuccessMessage("Invitation declined successfully.");
    await this.loadCompetitions();
  } catch (error) {
    this.showErrorMessage(error?.response?.data?.message || "Failed to decline invitation.");
  } finally {
    this.rejectResponse = "";
  }
},

// Helper method to update competition status locally
updateCompetitionStatus(competition, status) {
  // Update volunteerHistory
  if (competition.volunteerHistory) {
    const userId = this.profile._id;
    const entry = competition.volunteerHistory.find(
      entry => {
        if (entry.volunteer && entry.volunteer._id === userId) return true;
        if (entry.agency && entry.agency._id === userId) return true;
        return false;
      }
    );
    if (entry) {
      entry.response_status = status;
    }
  }
  
  // Also update requested_agency_ids if exists
  if (competition.requested_agency_ids) {
    const userId = this.profile._id;
    const req = competition.requested_agency_ids.find(
      r => r.user_id === userId || (r.user_id && r.user_id.$oid === userId)
    );
    if (req) {
      req.status = status === "Accepted" ? "Approved" : "Rejected";
      req.approved = status === "Accepted";
    }
  }
  
  this.$forceUpdate();
},

    async join(competition) {
      if (this.profile.type === "Volunteer") {
        this.joinDialog = true;
        this.currentCompetition = competition;
      } else {
        this.joinDialog = false;
        const params = {
          competition_id: competition._id
        };
        try {
          await this.joinCompetition(params);
          // Update local competition data so canJoin returns false
          if (!Array.isArray(competition.requested_agency_ids)) {
            this.$set(competition, "requested_agency_ids", []);
          }
          competition.requested_agency_ids.push({
            user_id: this.profile._id,
            approved: false,
            status: "Pending"
          });
          this.showSuccessMessage("Successfully joined the competition.");
        } catch (error) {
          const message = error || "Failed to join competition.";
          this.showErrorMessage(message);
        }
      }
    },
    // Data loading
    async loadCompetitions() {
      try {
        this.loading = true;
        await this.fetchAllCompetitions();
        this.getAllCompetitions.forEach(competition => {
          this.$set(competition, "showFullDescription", false);
        });
      } catch (error) {
        console.error("Error loading competitions:", error);
        this.showErrorMessage("Failed to load competitions.");
      } finally {
        this.loading = false;
      }
    },
    // Filter methods
    toggleStatusFilter(status) {
      if (status) {
        if (status === "all") {
          this.selectedStatuses = [];
        } else {
          const index = this.selectedStatuses.indexOf(status);
          if (index > -1) {
            this.selectedStatuses.splice(index, 1);
          } else {
            this.selectedStatuses.push(status);
          }
        }
        this.currentPage = 1;
      }
    },
    toggleStatusDropdown() {
      this.showStatusFilter = !this.showStatusFilter;
    },
    toggleDateFilter() {
      this.showDateFilter = !this.showDateFilter;
    },
    toggleSortFilter() {
      this.showSortFilter = !this.showSortFilter;
    },
    clearStatusFilter() {
      this.selectedStatuses = [];
      this.showStatusFilter = false;
      this.currentPage = 1;
    },
    clearDateFilter() {
      this.startDate = "";
      this.endDate = "";
      this.currentDatePreset = "";
      this.showDateFilter = false;
      this.currentPage = 1;
    },
    clearAllFilters() {
      this.searchQuery = "";
      this.selectedStatuses = [];
      this.startDate = "";
      this.endDate = "";
      this.currentDatePreset = "";
      this.currentSort = "status";
      this.currentPage = 1;

      // Close all filter dropdowns
      this.showStatusFilter = false;
      this.showDateFilter = false;
      this.showSortFilter = false;
    },
    removeStatusFilter(status) {
      const index = this.selectedStatuses.indexOf(status);
      if (index > -1) {
        this.selectedStatuses.splice(index, 1);
      }
      this.currentPage = 1;
    },
    applySorting(sortValue) {
      this.currentSort = sortValue;
      this.showSortFilter = false;
      this.currentPage = 1;
    },
    applyDatePreset(preset) {
      // Use user's timezone for "today"
      const timezone =
        localStorage.getItem("user-timezone") || "America/New_York";
      const today = this.convertTZ(new Date(), timezone);
      const formatDate = date => date.toISOString().split("T")[0];

      switch (preset) {
        case "today": {
          this.startDate = formatDate(today);
          this.endDate = formatDate(today);
          break;
        }
        case "week": {
          const startOfWeek = new Date(today);
          startOfWeek.setDate(today.getDate() - today.getDay());
          const endOfWeek = new Date(today);
          endOfWeek.setDate(today.getDate() - today.getDay() + 6);
          this.startDate = formatDate(startOfWeek);
          this.endDate = formatDate(endOfWeek);
          break;
        }
        case "month": {
          const startOfMonth = new Date(
            today.getFullYear(),
            today.getMonth(),
            1
          );
          const endOfMonth = new Date(
            today.getFullYear(),
            today.getMonth() + 1,
            0
          );
          this.startDate = formatDate(startOfMonth);
          this.endDate = formatDate(endOfMonth);
          break;
        }
        case "next30": {
          this.startDate = formatDate(new Date());
          const next30 = new Date();
          next30.setDate(next30.getDate() + 30);
          this.endDate = formatDate(next30);
          break;
        }
      }

      this.currentDatePreset = preset;
      this.currentPage = 1;
    },
    // Utility methods
    getStatusClass(competition) {
      switch (competition.competition_status) {
        case "Upcoming":
          return "status-upcoming";
        case "Ongoing":
          return "status-ongoing";
        case "Completed":
          return "status-completed";
        default:
          return "status-default";
      }
    },
    getJoinStatusClass(competition) {
      // Only for Agency
      if (
        this.profile.type === "Agency" &&
        Array.isArray(competition.requested_agency_ids)
      ) {
        const req = competition.requested_agency_ids.find(
          r =>
            r.user_id === this.profile._id ||
            (r.user_id && r.user_id.$oid === this.profile._id)
        );
        if (!req) return "";

        // Normalize status
        let status = req.status
          ? req.status.toLowerCase()
          : req.approved === true
            ? "approved"
            : req.approved === false
              ? "rejected"
              : "pending";

        // Return appropriate class based on status
        switch (status) {
          case "approved":
            return "status-joined";
          case "pending":
            return "status-pending";
          case "rejected":
            return "status-rejected";
          default:
            return "";
        }
      }
      return "";
    },

    getStatusIcon(status) {
      switch (status) {
        case "Upcoming":
          return "mdi-clock-outline";
        case "Ongoing":
          return "mdi-play-circle";
        case "Completed":
          return "mdi-check-circle";
        default:
          return "mdi-trophy";
      }
    },
    getStatusText(status) {
      const option = this.statusOptions.find(opt => opt.value === status);
      return option ? option.text : status;
    },
    isExpired(competition) {
      return new Date(competition.end_date) < new Date();
    },
    canDelete(competition) {
      return (
        this.profile.type === "Agency" &&
        competition.createdBy &&
        competition.createdBy._id === this.profile._id
      );
    },
    canEdit(competition) {
      const canEditResult = (
        this.profile.type === "Agency" &&
        competition.createdBy &&
        competition.createdBy._id === this.profile._id &&
        competition.competition_status !== "Completed" &&
        competition.competition_status !== "Ongoing"
      );
      return canEditResult;
    },
    canAddAgencies(competition) {
      return (
        this.profile.type === 'Agency' &&
        competition.createdBy &&
        competition.createdBy._id === this.profile._id &&
        competition.competition_status !== 'Completed' &&
        competition.competition_status !== 'Ongoing'
      );
    },
    onAddAgencies(competition) {
      this.currentCompetition = competition;
      this.addAgenciesDialog = true;
    },
    goToProfile(agencyId) {
      this.$router.push({
        name: "agency-profile-details",
        params: { id: agencyId }
      });
    },
    // Check if current user is the creator
    isCreator(competition) {
      if (!competition.createdBy) return false;
      return competition.createdBy._id === this.profile._id ||
        competition.createdBy === this.profile._id;
    },

    // Get the join type for the current user
    getUserJoinType(competition) {
      const userId = this.profile._id;

      // First check if user is the creator
      if (this.isCreator(competition)) {
        return "Creator";
      }

      // Check in requested_agency_ids (for agencies)
      if (this.profile.type === 'Agency' && competition.requested_agency_ids) {
        const found = competition.requested_agency_ids.find(
          req => req.user_id === userId || (req.user_id && req.user_id.$oid === userId)
        );
        if (found && found.join_type) {
          return found.join_type; // "Invitation" or "Request"
        }
      }

      // Check in volunteerHistory (for volunteers)
      if (competition.volunteerHistory && competition.volunteerHistory.length > 0) {
        const found = competition.volunteerHistory.find(
          entry => {
            // Check if the volunteer matches
            if (entry.volunteer && entry.volunteer._id === userId) {
              return true;
            }
            // Check if the agency matches (for agency users viewing volunteer history)
            if (entry.agency && entry.agency._id === userId) {
              return true;
            }
            return false;
          }
        );

        if (found) {
          // Check if join_type exists on the history entry
          if (found.join_type) {
            return found.join_type;
          }

          // Determine join type based on who initiated
          // If the user is the volunteer, check how they joined
          if (found.volunteer && found.volunteer._id === userId) {
            // If they were invited by an agency, it's "Invitation"
            // If they requested to join, it's "Request"
            // This depends on your data structure
            return "Request"; // Default
          }

          // If the user is an agency, check how they invited
          if (found.agency && found.agency._id === userId) {
            return "Invitation";
          }
        }
      }

      // If no join type found, check if user is in requested_volunteer_ids
      if (this.profile.type === 'Volunteer' && competition.requested_volunteer_ids) {
        const found = competition.requested_volunteer_ids.find(
          req => req.user_id === userId || (req.user_id && req.user_id.$oid === userId)
        );
        if (found) {
          return found.join_type || "Request";
        }
      }

      return null;
    },

    // Get user status from volunteerHistory
    getUserStatusFromHistory(competition) {
      // If user is the creator, return null (no status needed)
      if (this.isCreator(competition)) {
        return null;
      }

      if (!competition.volunteerHistory || competition.volunteerHistory.length === 0) {
        return null;
      }

      const userId = this.profile._id;

      // Check if user is a volunteer
      if (this.profile.type === 'Volunteer') {
        const historyEntry = competition.volunteerHistory.find(
          entry => entry.volunteer && entry.volunteer._id === userId
        );
        return historyEntry ? historyEntry.response_status : null;
      }

      // Check if user is an agency
      if (this.profile.type === 'Agency') {
        const historyEntry = competition.volunteerHistory.find(
          entry => entry.agency && entry.agency._id === userId
        );
        return historyEntry ? historyEntry.response_status : null;
      }

      return null;
    },

    // Modified getUserJoinStatus to hide for creator
    getUserJoinStatus(competition) {
      // If user is the creator, don't show any status
      if (this.isCreator(competition)) {
        return null;
      }

      // First check the volunteerHistory (this is the primary source)
      const status = this.getUserStatusFromHistory(competition);

      if (status) {
        return {
          status: status,
          class: this.getJoinStatusClassFromStatus(status),
          icon: this.getStatusIconForJoin(status)
        };
      }

      // Fallback: Check agency join status if no history entry
      if (this.profile.type === 'Agency') {
        const agencyStatus = this.getAgencyJoinStatus(competition);
        if (agencyStatus) {
          return {
            status: agencyStatus,
            class: this.getJoinStatusClassFromStatus(agencyStatus),
            icon: this.getStatusIconForJoin(agencyStatus)
          };
        }
      }

      return null;
    },

    // Modified canJoin to hide for creator
    // Modified canJoin to hide for creator
    canJoin(competition) {
      // If user is the creator, they can't join their own competition
      if (this.isCreator(competition)) {
        return false;
      }

      // Check if competition is completed or expired
      if (competition.competition_status === "Completed") return false;
      if (this.isExpired(competition)) return false;

      // Check if user already has a status in history (primary check)
      const userStatus = this.getUserStatusFromHistory(competition);
      if (userStatus) {
        return false; // Already has a status, can't join again
      }

      // Volunteer: check if already in requested_volunteer_ids
      if (this.profile.type === "Volunteer") {
        if (
          Array.isArray(competition.requested_volunteer_ids) &&
          competition.requested_volunteer_ids.some(
            req =>
            (req.user_id === this.profile._id ||
              (req.user_id && req.user_id.$oid === this.profile._id))
          )
        ) {
          return false;
        }
      }

      // Agency: check if already in requested_agency_ids
      if (this.profile.type === "Agency") {
        if (
          Array.isArray(competition.requested_agency_ids) &&
          competition.requested_agency_ids.some(
            req =>
              req.user_id === this.profile._id ||
              (req.user_id && req.user_id.$oid === this.profile._id)
          )
        ) {
          return false;
        }
      }

      return true;
    },

    // Get join status class from status
    getJoinStatusClassFromStatus(status) {
      const statusMap = {
        'Accepted': 'status-joined',
        'Pending': 'status-pending',
        'Rejected': 'status-rejected',
        'Approved': 'status-joined'
      };
      return statusMap[status] || '';
    },

    // Get icon for join status
    getStatusIconForJoin(status) {
      const iconMap = {
        'Accepted': 'mdi-check-circle',
        'Approved': 'mdi-check-circle',
        'Pending': 'mdi-clock-outline',
        'Rejected': 'mdi-close-circle'
      };
      return iconMap[status] || 'mdi-information';
    },
    getJoinStatus() {
      return null; // Placeholder
    },

    // Example method in CompetitionList.vue
    async agencyScores(competition) {
      try {
        const response = await this.topScores({
          competitionId: competition._id
        });
        this.topScoresData =
          response && response
            ? response
            : { topScoresofAgency: [], topScoresofVolunteers: [] };
        this.topScoresData.title = competition.title;
        this.overallagencyScoresDialog = true;
      } catch (e) {
        this.topScoresData = {
          topScoresofAgency: [],
          topScoresofVolunteers: []
        };
        this.showErrorMessage("Failed to load top scores");
      }
    },

    toggleAccordionFilter() {
      this.showAccordionFilter = !this.showAccordionFilter;
    },
    toggleAccordion(section) {
      this.accordionOpen[section] = !this.accordionOpen[section];
    },
    getDescription(competition) {
      if (!competition.description) return "No description available";

      if (
        competition.showFullDescription ||
        competition.description.length <= 100
      ) {
        return competition.description;
      }

      return competition.description.substring(0, 100) + "...";
    },
    toggleDescription(competition) {
      this.$set(
        competition,
        "showFullDescription",
        !competition.showFullDescription
      );
    },
    formatDate(date) {
      if (!date) return "Not specified";
      // Always display the date as the UTC calendar date, not shifted by timezone
      const d = typeof date === "string" ? new Date(date) : date;
      return d.toLocaleDateString("en-US", {
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "numeric",
        timeZone: "UTC" // <--- Force UTC calendar date
      });
    },
    convertTZ(date, tzString) {
      if (!date) return "";
      if (!tzString) {
        tzString = Intl.DateTimeFormat().resolvedOptions().timeZone;
      }
      return new Date(
        (typeof date === "string" ? new Date(date) : date).toLocaleString(
          "en-US",
          {
            timeZone: tzString
          }
        )
      );
    },
    getDuration(competition) {
      if (!competition.start_date || !competition.end_date) return "Not set";

      const start = new Date(competition.start_date);
      const end = new Date(competition.end_date);
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays === 1) return "1 day";
      if (diffDays < 7) return `${diffDays} days`;
      if (diffDays < 30)
        return `${Math.floor(diffDays / 7)} week${Math.floor(diffDays / 7) > 1 ? "s" : ""
          }`;
      return `${Math.floor(diffDays / 30)} month${Math.floor(diffDays / 30) > 1 ? "s" : ""
        }`;
    },
    // Event handlers
    handleClickOutside(event) {
      // Close the accordion filter if clicking outside
      const filterDropdown = this.$el.querySelector(".filter-dropdown");
      if (filterDropdown && !filterDropdown.contains(event.target)) {
        this.showAccordionFilter = false;
      }
      // Optionally, hide old dropdowns if you still use them
      this.showStatusFilter = false;
      this.showDateFilter = false;
      this.showSortFilter = false;
    }
  },
  watch: {
    searchQuery() {
      this.currentPage = 1; // Reset to first page when searching
    },
    startDate() {
      this.currentDatePreset = ""; // Clear preset when manually setting dates
      this.currentPage = 1;
    },
    endDate() {
      this.currentDatePreset = ""; // Clear preset when manually setting dates
      this.currentPage = 1;
    }
  },
  async created() {
    await this.loadCompetitions();
  },
  mounted() {
    // Add click outside listener for dropdowns
    document.addEventListener("click", this.handleClickOutside);
  },
  beforeDestroy() {
    // Remove click outside listener
    document.removeEventListener("click", this.handleClickOutside);
  }
};
</script>

<style scoped>
/* Modern Header Styles */
/* .modern-header {
  background: linear-gradient(135deg, #1390c0, #2c845a);
  color: white;
  padding: 2rem 0;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
} */
.modern-header {
  position: sticky;
  top: 50px;
  z-index: 1000;
  background: linear-gradient(135deg, #0a5b8a, #1da1f2);
  color: white;
  padding: 2rem 0;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  width: 100%;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
}

.header-left {
  flex: 1;
  min-width: 300px;
}

.page-title {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.title-icon {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 0.75rem;
  backdrop-filter: blur(10px);
}

.header-icon {
  color: white;
}

.title-text h1 {
  margin: 0;
  font-size: 2.5rem;
  font-weight: 700;
  line-height: 1.2;
}

.page-subtitle {
  margin: 0.5rem 0 0 0;
  opacity: 0.9;
  font-size: 1.1rem;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.actions-wrapper {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

/* Search Styles */
.search-container {
  position: relative;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  padding: 0.75rem 1rem;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  min-width: 270px;
}

.search-icon {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(255, 255, 255, 0.8);
}

.search-input {
  background: none;
  border: none;
  color: white;
  width: 100%;
  padding-left: 2rem;
  font-size: 0.95rem;
  outline: none;
}

.join-btn-text {
  color: #fff;
  font-size: 13px;
  font-weight: 500;
}

.search-input::placeholder {
  color: rgba(255, 255, 255, 0.7);
}

/* Filter Styles */
.filter-group {
  position: relative;
}

.filter-dropdown {
  position: relative;
}

.filter-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  padding: 0.75rem 1rem;
  color: white;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

.filter-btn:hover {
  background: rgba(255, 255, 255, 0.25);
  transform: translateY(-1px);
}

.filter-menu {
  position: absolute;
  top: calc(100% + 0.5rem);
  left: 0;
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  z-index: 2000;
  /* Increase z-index */
  min-width: 250px;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.1);
  display: inline-flex;
}

.filter-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  font-weight: 600;
  color: #495057;
}

.clear-btn {
  background: none;
  border: none;
  color: #6c757d;
  cursor: pointer;
  font-size: 0.85rem;
  text-decoration: underline;
}

.clear-btn:hover {
  color: #495057;
}

.filter-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: background-color 0.2s ease;
  color: #495057;
}

.filter-item:hover {
  background: #f8f9fa;
}

.filter-item.active-filter {
  background: #e3f2fd;
  color: #1976d2;
}

.status-indicator {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.status-upcoming {
  color: #2196f3;
}

.status-ongoing {
  color: #4caf50;
}

.status-completed {
  background: #9e9e9e;
}

.status-all {
  background: #2196f3;
}

.check-icon {
  margin-left: auto;
  color: #4caf50;
}

/* Date Filter Styles */
.date-filter-menu {
  min-width: 320px;
}

.accordion-section {
  border-bottom: 1px solid #e9ecef;
}

.accordion-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  cursor: pointer;
  font-weight: 600;
  color: #495057;
  background: #f8f9fa;
}

.accordion-content {
  padding: 0.5rem 1rem 1rem 1rem;
}

.date-inputs {
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.date-input-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.date-input-group label {
  font-size: 0.85rem;
  font-weight: 500;
  color: #6c757d;
}

.date-input {
  padding: 0.75rem;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  font-size: 0.9rem;
  outline: none;
  transition: border-color 0.2s ease;
}

.date-input:focus {
  border-color: #1976d2;
  box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.1);
}

.quick-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 1rem;
  border-top: 1px solid #e9ecef;
}

.preset-btn {
  padding: 0.5rem 0.75rem;
  background: #084468;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.preset-btn:hover {
  background: #084468;
}

.preset-btn.active {
  background: #1976d2;
  color: white;
  border-color: #1976d2;
}

/* View Toggle */
.view-toggle {
  display: flex;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  padding: 0.25rem;
  gap: 0.25rem;
}

.view-btn {
  padding: 0.5rem;
  background: none;
  border: none;
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.8);
  cursor: pointer;
  transition: all 0.2s ease;
}

.view-btn.active {
  background: rgba(255, 255, 255, 0.25);
  color: white;
}

.view-btn:hover:not(.active) {
  background: rgba(255, 255, 255, 0.1);
}

/* Action Buttons */
.action-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  color: white;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

.action-btn:hover {
  background: rgba(255, 255, 255, 0.25);
  transform: translateY(-1px);
}

.create-btn {
  background: linear-gradient(135deg, #1390c0, #2c845a);
  border: none;
  box-shadow: 0 4px 15px rgba(76, 175, 80, 0.3);
  border-radius: 20px;
  padding: 12px;
  border: 1px solid #fff;
  cursor: pointer;
  transition: all 0.3s ease;
  color: #fff;
}

.create-btn:hover {
  background: linear-gradient(135deg, #2c845a, #1390c0);
  box-shadow: 0 6px 20px rgba(76, 175, 80, 0.4);
}

.create-btn-text {
  color: #ffff;
  font-size: 15px;
  font-weight: bold;
}

/* Active Filters */
.active-filters {
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
}

.filter-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;
}

.filter-chip {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.2);
  padding: 0.5rem 0.75rem;
  border-radius: 20px;
  font-size: 0.85rem;
  color: white;
}

.chip-remove {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: white;
}

.clear-all-btn {
  background: rgba(244, 67, 54, 0.2);
  border: 1px solid rgba(244, 67, 54, 0.3);
  border-radius: 20px;
  padding: 0.5rem 1rem;
  color: white;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.clear-all-btn:hover {
  background: rgba(244, 67, 54, 0.3);
}

/* Results Summary */
.results-summary {
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  padding: 1rem 0;
  border-bottom-left-radius: 20px;
  border-bottom-right-radius: 20px;
  margin-top: 35px;
}

.summary-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
}

.results-count {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
}

.count {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1976d2;
}

.label {
  color: #6c757d;
  font-size: 0.9rem;
}

.summary-stats {
  display: flex;
  gap: 2rem;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
}

.stat-value {
  font-size: 1.25rem;
  font-weight: 600;
  color: #495057;
}

.stat-label {
  font-size: 0.8rem;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Main Content */
.main-content {
  padding: 2rem 1rem;
  max-width: 1200px;
  margin: 0 auto;
  padding-top: 25px;
}

/* Loading State */
.loading-state {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 1.5rem;
}

.skeleton-card {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.skeleton-header {
  height: 40px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  border-radius: 8px;
  margin-bottom: 1rem;
  animation: shimmer 2s infinite;
}

.skeleton-content {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.skeleton-line {
  height: 16px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  border-radius: 4px;
  animation: shimmer 2s infinite;
}

.skeleton-line.short {
  width: 60%;
}

@keyframes shimmer {
  0% {
    background-position: -200px 0;
  }

  100% {
    background-position: calc(200px + 100%) 0;
  }
}

/* Empty State */
.empty-state {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  padding: 2rem;
}

.empty-content {
  text-align: center;
  max-width: 400px;
}

.empty-icon {
  color: #bdbdbd;
  margin-bottom: 1rem;
}

.empty-content h3 {
  margin: 0 0 1rem 0;
  color: #ffff;
  font-size: 2.5rem;
}

.empty-content p {
  color: #ffff;
  margin-bottom: 2rem;
  line-height: 1.6;
}

.empty-actions {
  display: flex;
  justify-content: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.secondary-btn {
  padding: 10px 12px;
  background: white;
  /* border: 2px solid #e0e0e0; */
  border-radius: 30px;
  color: #424242;
  cursor: pointer;
  transition: all 0.2s ease;
}

.secondary-btn:hover {
  border-color: #bdbdbd;
  background: #f5f5f5;
}

/* Challenges Grid */
.challenges-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
  gap: 1.5rem;
}

.challenge-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: all 0.3s ease;
  cursor: pointer;
  border: 1px solid #f0f0f0;
}

.challenge-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
}

.challenge-card.expired-card {
  opacity: 0.7;
  background: #fafafa;
}

/* Challenges List */
.challenges-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.challenge-list-item {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
  cursor: pointer;
  border: 1px solid #f0f0f0;
}

.challenge-list-item:hover {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

/* Card Components */
/* .card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid #e9ecef;
} */

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 1.5rem;
  position: relative;
  border-top-left-radius: 11px;
  border-top-right-radius: 11px;
}

/* Flattened status modifiers */
/* .card-header.status-ongoing {
  background: linear-gradient(135deg, #0a5b8a, #4caf50);
}
.card-header.status-upcoming {
  background: linear-gradient(135deg, #0a5b8a, #1da1f2);
} */
.card-header.status-completed {
  background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%);
}

.card-header.status-default {
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
}

.card-header .status-badge {
  background: rgba(255, 255, 255, 0.25);
  color: white;
  padding: 0.375rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  backdrop-filter: blur(10px);
}

.status-badge {
  position: relative;
  overflow: hidden;
}

.status-badge::before {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg,
      transparent,
      rgba(255, 255, 255, 0.3),
      transparent);
  animation: shimmer 2s infinite;
}

@keyframes shimmer {
  0% {
    left: -100%;
  }

  50% {
    left: 100%;
  }

  100% {
    left: 100%;
  }
}

.card-header .card-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.card-header .action-btn {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  background: rgba(0, 149, 255, 0.96);
  border: none;
  color: white;
  padding: 0.5rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.75rem;
  backdrop-filter: blur(5px);
}

.card-header .action-btn:hover {
  background: rgba(0, 149, 255, 0.923);
  transform: scale(1.05);
}

.card-header .action-btn.join-btn {
  border-radius: 20px;
  padding: 10px 22px;
}

.card-header .action-btn.delete-btn:hover {
  background: rgba(239, 68, 68, 0.9);
}

.card-header .action-btn.edit-btn {
  background: rgba(255, 152, 0, 0.9);
}

.card-header .action-btn.edit-btn:hover {
  background: rgba(255, 152, 0, 1);
  transform: scale(1.05);
}

.status-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.status-badge {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
}

.status-upcoming .status-badge {
  background: rgb(255 134 0 / 76%);
  color: #ffffff;
}

.status-ongoing .status-badge {
  background: rgb(0 165 51);
  color: #ffffff;
}

.status-completed .status-badge {
  background: rgba(158, 158, 158, 0.1);
  color: #616161;
}

.expired-badge {
  background: rgba(244, 67, 54, 0.1);
  color: #d32f2f;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
}

.card-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.delete-btn {
  background: rgba(244, 67, 54, 0.1);
  color: #d32f2f;
  border: none;
  border-radius: 8px;
  padding: 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.delete-btn:hover {
  background: rgba(244, 67, 54, 0.2);
}

.join-btn {
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.8rem;
  box-shadow: #1da1f2 0px 0px 10px;
  transition: all 0.2s ease;
}

.join-btn:hover {
  background: rgba(76, 175, 80, 0.2);
}

.join-status {
  font-size: 0.75rem;
}

.status-joined {
  background: rgba(76, 175, 80, 0.1);
  color: #388e3c;
}

.status-pending {
  background: rgba(255, 193, 7, 0.1);
  color: #f57c00;
}

.status-rejected {
  background: rgba(244, 67, 54, 0.1);
  color: #d32f2f;
}

.card-content {
  padding: 2px 19px 0px 30px;
  display: flex;
  flex-direction: row;
}

.challenge-icon {
  /* text-align: center; */
  margin-bottom: 1rem;
  padding: 20px 19px 12px 1px;
}

.challenge-details {
  margin-bottom: 1rem;
}

.creator-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0;
}

.creator-avatar {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  overflow: hidden;
  background: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.creator-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.creator-details {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.creator-name {
  font-weight: 500;
  color: #424242;
}

.creator-type {
  font-size: 0.8rem;
  color: #757575;
}

.challenge-title {
  background: linear-gradient(to right, #3b82f6, #10b981);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-size: 1.25rem;
  font-weight: 700;
  margin-bottom: 0.75rem;
  line-height: 1.3;
}

.challenge-description {
  color: #6b7280;
  line-height: 1.6;
  margin-bottom: 0.5rem;
}

.toggle-btn {
  background: none;
  border: none;
  color: #1976d2;
  font-size: 0.85rem;
  cursor: pointer;
  padding: 0.25rem 0;
  text-decoration: underline;
  transition: color 0.2s ease;
}

.toggle-btn:hover {
  color: #0d47a1;
}

.divider {
  height: 1px;
  background: #e9ecef;
  margin: 0 1.5rem;
}

.challenge-metadata {
  padding: 1rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.date-info {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
}

.date-item {
  display: inline-flex;
  align-items: center;
  gap: 1.5rem;
  flex: -1;
}

.date-icon {
  color: #6c757d;
}

.date-details {
  display: flex;
  flex-direction: column;
}

.date-label {
  font-size: 0.75rem;
  color: #6c757d;
}

.date-value {
  font-size: 0.9rem;
  font-weight: 500;
  color: #495057;
}

.top-performers {
  background: linear-gradient(135deg, #f0f7ff, #e0f2fe);
  border-radius: 10px;
  padding: 1rem;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #495057;
  font-weight: 500;
  margin-bottom: 0.75rem;
}

.crown-icon {
  color: #ffc107;
}

.performer-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
}

.performer-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.performer-icon {
  color: #6c757d;
}

.performer-name {
  font-size: 0.9rem;
  color: #495057;
}

.performer-score {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.85rem;
  font-weight: 500;
  color: #388e3c;
}

.no-scores {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #6c757d;
  font-size: 0.9rem;
  padding: 1rem 0;
  justify-content: center;
}

/* Pagination */
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin-top: 3rem;
}

.pagination-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  color: #495057;
  cursor: pointer;
  transition: all 0.2s ease;
}

.pagination-btn:hover:not(:disabled) {
  background: #f5f5f5;
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-numbers {
  display: flex;
  gap: 0.5rem;
}

.page-btn {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: white;
  border: 1px solid #e0e0e0;
  color: #495057;
  cursor: pointer;
  transition: all 0.2s ease;
}

.page-btn:hover {
  background: #f5f5f5;
}

.page-btn.active {
  background: #1976d2;
  color: white;
  border-color: #1976d2;
}

/* Snackbar */
.snackbar-content {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.snackbar-icon {
  margin-right: 0.5rem;
}

/* Account Status Message */
.status-message {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 80vh;
  padding: 2rem;
}

.status-card {
  text-align: center;
  max-width: 500px;
  padding: 3rem;
  background: white;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
}

.status-card h2 {
  margin: 1.5rem 0 1rem;
  color: #424242;
}

.status-card p {
  color: #757575;
  margin-bottom: 2rem;
  line-height: 1.6;
}

.back-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: #1976d2;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.back-btn:hover {
  background: #1565c0;
}

/* Responsive Adjustments */
@media (max-width: 992px) {
  .header-content {
    flex-direction: column;
  }

  .header-left,
  .header-right {
    width: 100%;
  }

  .actions-wrapper {
    justify-content: space-between;
    width: 100%;
  }

  .challenges-grid {
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  }
}

@media (max-width: 768px) {
  .date-info {
    flex-direction: column;
    gap: 0.75rem;
  }

  .pagination {
    flex-wrap: wrap;
  }

  .page-numbers {
    order: 1;
    width: 100%;
    justify-content: center;
    margin: 0.5rem 0;
  }
}

@media (max-width: 576px) {
  .search-container {
    min-width: 100%;
  }

  .filter-btn span {
    display: none;
  }

  .challenge-card,
  .challenge-list-item {
    min-width: 100%;
  }

  .status-card {
    padding: 2rem 1.5rem;
  }
}

/*  */

.elite-leaderboard {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 14px;
  padding: 16px;
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

.leaderboard-title {
  margin: 0 0 12px 0;
  font-size: 16px;
  color: #fe0000;
  text-align: center;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.leaderboard-row {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.liquid-card {
  flex: 1;
  min-width: 160px;
  display: flex;
  align-items: center;
  padding: 12px 15px;
  border-radius: 10px;
  position: relative;
  overflow: hidden;
}

.liquid-card::before {
  content: "";
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: linear-gradient(to bottom right,
      rgba(255, 255, 255, 0.3),
      rgba(255, 255, 255, 0));
  transform: rotate(30deg);
  animation: liquid 6s infinite linear;
}

.agency {
  background: linear-gradient(135deg, #3a7bd5, #0e0e0e);
}

.volunteer {
  background: linear-gradient(135deg, #f46b45, #0e0e0e);
}

@keyframes liquid {
  0% {
    transform: rotate(30deg) translate(-30%, -30%);
  }

  100% {
    transform: rotate(30deg) translate(30%, 30%);
  }
}

.liquid-icon {
  font-size: 24px;
  margin-right: 12px;
  filter: drop-shadow(0 2px 3px rgba(0, 0, 0, 0.2));
}

.liquid-details {
  flex: 1;
  color: white;
}

.liquid-details span {
  display: block;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 1px;
  opacity: 0.8;
  margin-bottom: 2px;
}

.liquid-details strong {
  font-weight: 600;
  font-size: 14px;
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.liquid-points {
  font-weight: 800;
  font-size: 18px;
  color: white;
  margin-left: 8px;
  text-shadow: 0 2px 3px rgba(0, 0, 0, 0.2);
}

.liquid-points small {
  font-size: 10px;
  font-weight: normal;
  margin-left: 2px;
  opacity: 0.8;
}

.empty-leaderboard {
  text-align: center;
  padding: 0px;
  color: rgb(255 11 11 / 60%);
  font-style: italic;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 10px;
}

.challenge-card {
  position: relative;
  overflow: hidden;
}

.challenge-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg,
      transparent,
      rgba(59, 130, 246, 0.1),
      transparent);
  transition: left 0.5s;
  z-index: 1;
  pointer-events: none;
}

.challenge-card:hover::before {
  left: 100%;
}

.status-badge.join-status {
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  text-transform: capitalize;
}

.status-joined {
  background-color: rgba(76, 175, 80, 0.2);
  color: #2e7d32;
  border: 1px solid rgba(76, 175, 80, 0.3);
}

.status-pending {
  background-color: rgba(255, 193, 7, 0.2);
  color: #ff8f00;
  border: 1px solid rgba(255, 193, 7, 0.3);
}

.status-rejected {
  background-color: rgba(244, 67, 54, 0.2);
  color: #c62828;
  border: 1px solid rgba(244, 67, 54, 0.3);
}

.join-status-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  text-transform: capitalize;
  margin-left: 8px;
  border: 1px solid transparent;
}

.join-status-badge .status-icon {
  margin-right: 2px;
}

/* Approved status */
.join-status-badge.status-joined {
  background-color: rgba(76, 175, 80, 0.2) !important;
  color: #2e7d32 !important;
  border-color: rgba(76, 175, 80, 0.3) !important;
}

/* Pending status */
.join-status-badge.status-pending {
  background-color: rgba(255, 193, 7, 0.2) !important;
  color: #ff8f00 !important;
  border-color: rgba(255, 193, 7, 0.3) !important;
}

/* Rejected status */
.join-status-badge.status-rejected {
  background-color: rgba(244, 67, 54, 0.2) !important;
  color: #c62828 !important;
  border-color: rgba(244, 67, 54, 0.3) !important;
}

/* Join Type Badge */
.join-type-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: rgba(255, 255, 255, 0.2);
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 500;
  color: #0895F9;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  backdrop-filter: blur(4px);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

/* Update status-info to handle the new badge */
.status-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

/* Status Badge */
.status-badge {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
  background: rgba(255, 255, 255, 0.25);
  color: white;
  backdrop-filter: blur(10px);
}

/* Status-specific styles */
.status-upcoming .status-badge {
  background: rgb(255, 136, 0);
  color: #ffffff;
}

.status-ongoing .status-badge {
  background: rgba(0, 165, 51, 0.8);
  color: #ffffff;
}

.status-completed .status-badge {
  background: rgba(158, 158, 158, 0.8);
  color: #ffffff;
}

/* Accept & Reject Button Styles */
.action-btn.accept-btn {
  background: linear-gradient(135deg, #10b981, #059669);
  border: none;
  color: white;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  transition: all 0.3s ease;
}

.action-btn.accept-btn:hover {
  background: linear-gradient(135deg, #059669, #047857);
  transform: scale(1.05);
  box-shadow: 0 4px 15px rgba(16, 185, 129, 0.4);
}

.action-btn.accept-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none !important;
}

.action-btn.reject-btn {
  background: linear-gradient(135deg, #ef4444, #dc2626);
  border: none;
  color: white;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  transition: all 0.3s ease;
}

.action-btn.reject-btn:hover {
  background: linear-gradient(135deg, #dc2626, #b91c1c);
  transform: scale(1.05);
  box-shadow: 0 4px 15px rgba(239, 68, 68, 0.4);
}

.action-btn.reject-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none !important;
}

.action-btn .action-btn-text {
  font-size: 12px;
  font-weight: 600;
}

/* Card header action buttons container */
.card-header .action-btn {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  border: none;
  color: white;
  padding: 0.5rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.75rem;
  backdrop-filter: blur(5px);
}

.card-header .action-btn.join-btn {
  border-radius: 20px;
  padding: 8px 18px;
  background: linear-gradient(135deg, #3b82f6, #2563eb);
}

.card-header .action-btn.join-btn:hover {
  background: linear-gradient(135deg, #2563eb, #1d4ed8);
  transform: scale(1.05);
  box-shadow: 0 4px 15px rgba(59, 130, 246, 0.4);
}

.card-header .action-btn.delete-btn {
  background: rgba(239, 68, 68, 0.9);
}

.card-header .action-btn.delete-btn:hover {
  background: rgba(239, 68, 68, 1);
  transform: scale(1.05);
}

.card-header .action-btn.edit-btn {
  background: rgba(255, 152, 0, 0.9);
}

.card-header .action-btn.edit-btn:hover {
  background: rgba(255, 152, 0, 1);
  transform: scale(1.05);
}

.card-header .action-btn.add-agencies-btn {
  background: rgba(139, 92, 246, 0.9);
}

.card-header .action-btn.add-agencies-btn:hover {
  background: rgba(139, 92, 246, 1);
  transform: scale(1.05);
}

</style>
